<template>
  <div class="sidebar-content">
    <div class="sidebar-header">
      <el-button
          type="primary"
          icon="el-icon-plus"
          circle
          size="small"
          @click="handleNewChat"
          class="new-chat-button tech-neon-btn"
          :title="$t('chat.sidebar.newChatButton')">
      </el-button>
      <span v-if="!isCollapsed" class="header-title">{{ $t('chat.sidebar.title') }}</span>
    </div>

    <div class="history-list">
      <div
          v-for="item in historyItems"
          :key="item.id"
          :class="['history-item', { 'is-selected': item.id === selectedChatId }, { 'is-collapsed': isCollapsed }]"
          @click="selectChat(item.id)"
          @mouseover="hoverItemId = item.id"
          @mouseleave="hoverItemId = null"
      >
        <i class="el-icon-chat-dot-round history-icon"></i>
        <span v-if="!isCollapsed" class="history-title">{{ getDisplayTitle(item) }}</span>

        <div v-if="!isCollapsed && (item.id === selectedChatId || hoverItemId === item.id)" class="history-actions">
          <i class="el-icon-edit" @click.stop="renameChat(item.id)" :title="$t('chat.actions.rename')"></i>
          <i class="el-icon-delete" @click.stop="deleteChat(item.id)" :title="$t('chat.actions.delete')"></i>
        </div>
      </div>
      <div v-if="!isCollapsed && showLoadMore" class="load-more-history" @click="loadMore">
        {{ $t('chat.sidebar.loadMore') }}
      </div>
      <div v-if="!isCollapsed && historyItems.length > 0 && !showLoadMore" class="no-more-history">
        {{ $t('chat.sidebar.endOfHistory') }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChatSidebar',
  props: {
    isCollapsed: {
      type: Boolean,
      default: false
    },
    historyItems: {
      type: Array,
      default: () => []
    },
    selectedChatId: {
      type: Number,
      default: null
    },
    showLoadMore: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isMobile: false,
      hoverItemId: null
    }
  },
  methods: {
    getDisplayTitle(item) {
      const lang = this.$i18n.locale;
      if (lang === 'zh' && item.titleZh) return item.titleZh;
      if (lang === 'en' && item.titleEn) return item.titleEn;
      return item.title;
    },
    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    handleNewChat() {
      this.$emit('new-chat');
    },
    selectChat(chatId) {
      if (this.selectedChatId !== chatId) {
        this.$emit('select-chat', chatId);
      }
    },
    renameChat(chatId) {
      this.$emit('rename-chat', chatId);
    },
    deleteChat(chatId) {
      this.$emit('delete-chat', chatId);
    },
    loadMore() {
      this.$emit('load-more');
    }
  },
  mounted() {
    this.checkIsMobile();
    window.addEventListener('resize', this.checkIsMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIsMobile);
  }
};
</script>

<style scoped>
.sidebar-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: transparent; /* 侧边栏本身透明 */
}

.sidebar-header {
  padding: 15px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(0, 240, 255, 0.1); /* 微弱的分割线 */
  flex-shrink: 0;
}

/* 霓虹按钮样式 */
.tech-neon-btn {
  background: rgba(0, 240, 255, 0.1) !important;
  border: 1px solid var(--tech-primary) !important;
  color: var(--tech-primary) !important;
  box-shadow: 0 0 5px rgba(0, 240, 255, 0.2);
  transition: all 0.3s ease;
}
.tech-neon-btn:hover {
  background: var(--tech-primary) !important;
  color: #000 !important;
  box-shadow: 0 0 15px var(--tech-primary);
}

.new-chat-button {
  margin-right: 10px;
}

.header-title {
  margin-left: 5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--tech-text-main);
  letter-spacing: 1px;
}

.history-list {
  padding: 10px;
  flex-grow: 1;
  overflow-y: auto;
  /* 隐藏默认滚动条样式，由 App.vue 全局样式接管 */
}

/* 历史记录项：HUD 风格 */
.history-item {
  padding: 12px 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  color: var(--tech-text-sub); /* 默认灰蓝色 */
  border-radius: 4px;
  margin-bottom: 5px;
  position: relative;
  border: 1px solid transparent; /* 预留边框位置 */
}

/* 悬停状态：微光 + 边框显现 */
.history-item:hover {
  background-color: rgba(0, 240, 255, 0.05);
  color: var(--tech-text-main);
  border-color: rgba(0, 240, 255, 0.3);
}

/* 选中状态：强光高亮 + 渐变背景 */
.history-item.is-selected {
  background: linear-gradient(90deg, rgba(0, 240, 255, 0.15) 0%, transparent 100%);
  color: var(--tech-primary);
  border-left: 3px solid var(--tech-primary); /* 左侧高亮指示条 */
  border-color: rgba(0, 240, 255, 0.1);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
}

.history-item.is-collapsed {
  justify-content: center;
  padding: 12px 0;
  margin: 0 5px 5px 5px;
}

.history-icon {
  margin-right: 10px;
  min-width: 18px;
  font-size: 18px;
  flex-shrink: 0;
  opacity: 0.8;
}

.history-item.is-selected .history-icon {
  opacity: 1;
  text-shadow: 0 0 5px var(--tech-primary);
}

.history-item.is-collapsed .history-icon {
  margin-right: 0;
}

.history-item.is-collapsed .history-title {
  display: none;
}

.history-title {
  flex-grow: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 10px;
  font-size: 14px;
}

.history-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.history-actions i {
  margin-left: 8px;
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  color: var(--tech-text-sub);
  transition: color 0.2s ease;
}

.history-actions i:hover {
  color: var(--tech-primary);
  text-shadow: 0 0 5px var(--tech-primary);
}

.history-actions i:first-child {
  margin-left: 0;
}

.is-collapsed .sidebar-header {
  justify-content: center;
}

.is-collapsed .new-chat-button {
  margin-right: 0;
}

.load-more-history, .no-more-history {
  text-align: center;
  padding: 15px 10px;
  font-size: 13px;
  color: var(--tech-text-sub);
  opacity: 0.7;
}

.load-more-history {
  cursor: pointer;
  color: var(--tech-primary);
  transition: all 0.2s ease;
}

.load-more-history:hover {
  color: #fff;
  text-shadow: 0 0 5px var(--tech-primary);
}
</style>